import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import formatPrice from '../../utils/formatPrice';
import { swatchBackground } from '../../utils/colors';
import { useAuth } from '../../contexts/AuthContext';
import { useCart } from '../../contexts/CartContext';
import { useToast } from '../../contexts/ToastContext';
import { useWishlist } from '../../contexts/WishlistContext';
import '../../css/productCard.css';
import { mediaUrl } from '../../utils/mediaUrl';

const cleanProductName = (raw) => {
  if (!raw || typeof raw !== 'string') return '';
  let str = raw.trim();

  // Strip leading emojis or decorative symbols (e.g. 💖, ✨, 🌸)
  str = str.replace(/^[\p{Extended_Pictographic}\uFE0F\u200D\s]+/gu, '').trim();

  // Fix repeated title bug (e.g. "Nuoflexx Red Ribbed-Textured T-ShirtNuoflexx Red Ribbed-Textured...")
  const len = str.length;
  if (len >= 10) {
    const half = Math.floor(len / 2);
    if (len % 2 === 0 && str.slice(0, half) === str.slice(half)) {
      str = str.slice(0, half);
    } else {
      for (let i = half; i >= 6; i--) {
        const candidate = str.slice(0, i);
        if (str.slice(i).startsWith(candidate)) {
          str = candidate;
          break;
        }
      }
    }
  }

  return str;
};

const ProductCard = ({ product, hideBadge = false, hideMeta = false }) => {
  const img = mediaUrl(product.images?.[0] || '');
  const [imgError, setImgError] = useState(false);
  const subLabel = product.subCategory || product.category?.name || '';
  const displayName = cleanProductName(product.name);

  // Textile catalog products don't carry mrp/price at the top level —
  // only each variant does. Fall back to the first variant so the
  // discount badge actually shows up for textile items too.
  const firstVariant = product.variants?.[0];
  const price = product.price ?? product.retailPriceMin ?? firstVariant?.retailPrice ?? 0;
  const mrp = product.mrp ?? firstVariant?.mrp ?? null;

  const hasDiscount = mrp && mrp > price;
  const discountPercent = hasDiscount
    ? Math.round(((mrp - price) / mrp) * 100)
    : null;

  // Color comes as either a single "color" string or a "colors" array
  // depending on catalog type.
  const colorLabel = product.colors?.length
    ? product.colors.join(', ')
    : product.color || '';
  const swatchColor = swatchBackground(product.colors?.[0] || product.color || '');

  const { user } = useAuth();
  const { addToCart } = useCart();
  const [cartLoading, setCartLoading] = useState(false);
  const navigate = useNavigate();
  const { addToast } = useToast();
  const { isInWishlist, toggleWishlist, loadingIds } = useWishlist();

  const wishActive = isInWishlist(product._id);
  const wishLoading = loadingIds.includes(product._id.toString());

  // badge logic
  let badgeLabel = null;
  let badgeClass = null;

  if (!hideBadge) {
    if (product.isNewArrival) {
      badgeLabel = 'New';
      badgeClass = 'pc-badge-new';
    } else if (product.isBestSeller) {
      badgeLabel = 'Best Seller';
      badgeClass = 'pc-badge-best';
    } else if (product.isTrending) {
      badgeLabel = 'Trending';
      badgeClass = 'pc-badge-trending';
    }
  }

  const handleWishlistClick = async (e) => {
    e.preventDefault();

    if (!user) {
      addToast('Please log in to add product to wishlist', 'error');
      navigate('/auth');
      return;
    }

    if (wishLoading) return;

    try {
      const nowInWishlist = await toggleWishlist(product._id);

      if (nowInWishlist) {
        addToast('Added to your wishlist', 'success');
      } else {
        addToast('Removed from your wishlist', 'info');
      }
    } catch (err) {
      addToast('Could not update your wishlist. Please try again.', 'error');
    }
  };

  const handleAddToCart = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!user) {
      addToast('Please log in to add product to cart', 'error');
      navigate('/auth');
      return;
    }

    if (cartLoading) return;

    const targetUrl = product.slug
      ? `/product/${product.slug}`
      : `/product/${product._id}${product.sleeveOrStyle || product.subCategory || product.catalogType === "textile" ? "?textile=true" : ""}`;

    // If the product has multiple sizes, navigate so user can select their size
    const availableSizes = product.variants?.length
      ? product.variants.map((v) => v.size).filter(Boolean)
      : product.sizes || [];

    if (availableSizes.length > 1) {
      navigate(targetUrl);
      return;
    }

    const defaultSize = availableSizes[0] || 'Free Size';
    const isTextile = product.catalogType === 'textile' || Boolean(product.variants?.length);

    try {
      setCartLoading(true);
      await addToCart({
        productId: product._id,
        qty: 1,
        size: defaultSize,
        color: product.colors?.[0] || product.color || '',
        catalogType: isTextile ? 'textile' : 'legacy'
      });
      addToast('Added to cart successfully', 'success');
    } catch (err) {
      addToast(err?.response?.data?.message || 'Could not add to cart. Please try again.', 'error');
    } finally {
      setCartLoading(false);
    }
  };

  const showImage = img && !imgError;

  return (
    <Link
      to={product.slug ? `/product/${product.slug}` : `/product/${product._id}${product.sleeveOrStyle || product.subCategory || product.catalogType === "textile" ? "?textile=true" : ""}`}
      className="pc-card"
    >
      <div className="pc-card-inner">
        <div className="pc-image-wrapper">
          <div className="pc-top">
            <div className="pc-badge-container">
              {badgeLabel && (
                <span className={`pc-badge ${badgeClass}`}>{badgeLabel}</span>
              )}
            </div>

            <div className="pc-wishlist-container">
              <button
                type="button"
                aria-label={wishActive ? 'Remove from wishlist' : 'Add to wishlist'}
                aria-pressed={wishActive}
                className={`pc-wishlist-btn ${
                  wishActive ? 'pc-wishlist-active' : ''
                }`}
                onClick={handleWishlistClick}
                disabled={wishLoading}
              >
                ♥
              </button>
            </div>
          </div>

          {showImage ? (
            <img
              src={img}
              alt={product.name}
              className="pc-image"
              loading="lazy"
              decoding="async"
              width={400}
              height={500}
              onError={() => {
                setImgError(true);
              }}
            />
          ) : (
            <div className="pc-image pc-placeholder">Image</div>
          )}
        </div>

        <div className="pc-info">
          <div className="pc-name" title={displayName}>
            {displayName}
          </div>

          {!hideMeta && (subLabel || colorLabel) && (
            <div className="pc-meta-row">
              {subLabel && <span className="pc-subcategory">{subLabel}</span>}
              {colorLabel && (
                <span className="pc-color">
                  <span
                    className="pc-color-swatch"
                    style={{ background: swatchColor }}
                  />
                  {colorLabel}
                </span>
              )}
            </div>
          )}

          <div className="pc-price-row">
            <span className="pc-price">{formatPrice(price)}</span>
            {mrp && (
              <span className="pc-mrp">{formatPrice(mrp)}</span>
            )}
            {discountPercent && (
              <span className="pc-discount">{discountPercent}% off</span>
            )}
          </div>

          <button
            type="button"
            className="pc-add-cart-btn"
            onClick={handleAddToCart}
            disabled={cartLoading}
          >
            {cartLoading ? 'Adding...' : 'Add to Cart'}
          </button>
        </div>
      </div>
    </Link>
  );
};

export default ProductCard;
